// ActiveRideOverlay
// =================
//
// Wave 14ca-fix41: Top-of-Map-Overlay waehrend einer aktiven Free-Ride-
// Aufzeichnung. Zeigt km + Hoehenmeter gross lesbar, dazu Stop-Button.
//
// Design-Anspruch (User-Wunsch): "grosse schrift aber nicht zu viel karte
// verdecken". Kompromiss:
//   - Dunkle, halbtransparente Pille (turbo-900/85, backdrop-blur), gleicher
//     visueller Stil wie BottomBar
//   - Eine kompakte Zeile: rotes REC-Dot + grosse km-Zahl + ↑m-Zahl + Stop
//   - Sitzt safe-area-aware oben im Map-Viewport, fixed-position via Portal
//     ist unnoetig (das Overlay haengt am gleichen Stacking-Context wie die
//     BottomBar — beide sind Map-Children).
//   - Tap-Targets ≥ 44px (Stop-Button) damit er auch mit Handschuhen
//     erreichbar ist.
//
// Sichtbarkeit:
//   - Rendered NUR wenn der rideRecorder im 'recording'-State ist.
//   - Beim Stoppen leitet onStop den User auf /my-rides → letzter Ride
//     direkt sichtbar. Confirm-Dialog vorher gegen versehentliches Tappen.
//
// Wave 14ca-fix64: Stop-Confirm war bisher window.confirm("Stop and save?").
// Das hat nur OK / Abbrechen — keine Moeglichkeit zu stoppen OHNE zu
// speichern. User-Feedback: "ich kann nicht sagen stop und save NICHT,
// schlechte UI". Loesung: 3-Button-Modal (analog OneWayPicker in
// DiscoveryButtons): "Speichern" / "Verwerfen" / "Weiterfahren".

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { rideRecorder } from '../lib/rideRecorder';
import { useRecorderState } from '../lib/useRideRecorder';
import { useCurrentPosition } from '../lib/useCurrentPosition';
import { kmhFromCoords, formatKmh } from '../lib/speed';
import { useAdminMode, useDiscoveryMode } from '../lib/useUiSettings';
import { batchMarkApprovedFromPositions } from '../lib/discoveryApi';
import { listEndedRides } from '../lib/rideRecorder';

export function ActiveRideOverlay() {
  const state = useRecorderState();
  const navigate = useNavigate();
  const [adminMode] = useAdminMode();
  const [discoveryMode] = useDiscoveryMode();
  // Wave 14ca-fix64: Stop-Modal. true → Modal offen, Recording laeuft weiter
  // bis User explizit auf Save/Discard tippt.
  const [showStopModal, setShowStopModal] = useState(false);
  // Aktuelle Geschwindigkeit (km/h) aus dem GPS-Fix — live in der Pille.
  const pos = useCurrentPosition();
  const speedKmh = formatKmh(kmhFromCoords(pos));

  if (state.kind !== 'recording') return null;
  const { stats } = state;
  const km = (stats.distanceMeters / 1000).toFixed(2);
  // Wave 14cf-fix20: Live-Anzeige addiert pending-Climb auf den committed
  // gain — sonst bleibt die Höhenmeter-Zahl 0 bis zur ersten Reversal um
  // ≥ ELEVATION_HYSTERESIS_THRESHOLD_M (was bei monoton bergauf gefahrenen
  // Tour-Phasen ewig dauern kann). Bei Reversal-Commit wandert pending in
  // committed; die Summe bleibt identisch → kein UI-Flicker. Final saved
  // ride: computeStats() hat einen Tail-Handler der den noch-offenen Run
  // ohnehin verbucht, d.h. das hier ist rein UX-Polish, keine Doppel-
  // zählung in der gespeicherten Aggregat-Stat.
  const climbM = Math.round(stats.elevationGainM + stats.pendingClimbM);
  // Wave 14cf-fix6: Moving-Time-Anzeige. Format: <1h → "12m", ≥1h → "1h23".
  // Bewusst kompakt damit's links neben km/hm in dieselbe Zeile passt.
  const movingTimeText = formatMovingTime(stats.movingTimeMs);

  const handleSave = async () => {
    setShowStopModal(false);
    const id = await rideRecorder.stop();
    if (id) {
      // Wave 14ca-fix58: Auto-Approve aller traversierten OSM-Ways wenn
      // Discovery-Mode + Admin-Mode aktiv waren. Fire-and-forget — User
      // landet sofort in /my-rides, der Batch laeuft im Hintergrund.
      if (adminMode && discoveryMode) {
        void (async () => {
          try {
            const all = await listEndedRides();
            const ride = all.find((r) => r.id === id);
            if (!ride) return;
            const positions = ride.points.map((p) => ({
              lat: p.lat,
              lon: p.lon,
            }));
            const result = await batchMarkApprovedFromPositions(positions);
            if (result) {
              console.info(
                `[discovery] auto-approved ${result.applied} ways (${result.skipped} skipped)`,
              );
            }
          } catch (err) {
            console.warn('[discovery] batch on stop failed', err);
          }
        })();
      }
      // direkter Sprung in My-Rides damit der User die frische Ride sieht
      navigate('/my-rides');
    }
  };

  /** Discard — Recording verwerfen, IDB-Eintrag loeschen, kein Navigate
   *  (User bleibt auf der Map). Doppelt confirmen weil's destruktiv ist:
   *  Recording-Daten sind nach discard() weg, nicht wiederherstellbar. */
  const handleDiscard = async () => {
    const reallyDiscard = window.confirm(
      `Really discard? The ${km} km / ↑${climbM} m will be lost — this cannot be undone.`,
    );
    if (!reallyDiscard) return;
    setShowStopModal(false);
    await rideRecorder.discard();
    // Kein Navigate — User bleibt wo er ist, kann sofort einen neuen Ride
    // starten oder weiter erkunden.
  };

  const handleStop = () => setShowStopModal(true);

  // Wave 14cf-fix6: bündig nach links (User-Wunsch). Vorher: links 8.5rem
  // damit der Style-Switcher daneben passt. Jetzt: links 0.75rem (volle
  // linke Kante), Style-Switcher + PLUS-Menu wandern eine Reihe nach unten
  // (siehe MapPage's recording-aware Top-Padding).
  // Reihenfolge in der Pille (von links nach rechts):
  //   REC-Dot · Moving-Time · Distanz · Höhenmeter · Stop-Button
  return (
    <>
      <div
        className="pointer-events-none absolute z-30 flex"
        style={{
          top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
          left: '0.75rem',
          right: '4rem',
        }}
      >
        <div className="pointer-events-auto bg-turbo-900/85 backdrop-blur-md border border-turbo-500/40 rounded-xl pl-2.5 pr-1 py-1 shadow-lg flex items-center gap-1.5 h-10 max-w-full">
          <span className="relative flex items-center justify-center w-3 h-3 shrink-0">
            <span className="absolute w-3 h-3 rounded-full bg-red-500/40 animate-ping" />
            <span className="relative w-2.5 h-2.5 rounded-full bg-red-500" />
          </span>
          <div className="flex items-baseline gap-1.5 leading-none flex-1 min-w-0">
            {/* Aktuelle Geschwindigkeit ganz vorne — wichtigster Wert beim Fahren. */}
            <div className="flex items-baseline gap-0.5">
              <span className="text-white text-base font-bold tabular-nums">
                {speedKmh}
              </span>
              <span className="text-white/55 text-[11px] font-medium">km/h</span>
            </div>
            <span className="text-white/30 text-[11px]">·</span>
            {/* Moving-Time — 14cf-fix6. */}
            <div className="flex items-baseline gap-0.5">
              <span className="text-white text-base font-bold tabular-nums">
                {movingTimeText}
              </span>
            </div>
            <span className="text-white/30 text-[11px]">·</span>
            <div className="flex items-baseline gap-0.5">
              <span className="text-white text-base font-bold tabular-nums">
                {km}
              </span>
              <span className="text-white/55 text-[11px] font-medium">km</span>
            </div>
            <span className="text-white/30 text-[11px]">·</span>
            <div className="flex items-baseline gap-0.5">
              <span className="text-white/55 text-[11px]">↑</span>
              <span className="text-white text-base font-bold tabular-nums">
                {climbM}
              </span>
              <span className="text-white/55 text-[11px] font-medium">m</span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleStop}
            className="ml-auto shrink-0 bg-red-500/95 hover:bg-red-400 active:bg-red-600 text-white text-[13px] font-semibold px-3 h-8 rounded-lg flex items-center justify-center gap-1 transition-colors leading-none"
            aria-label="Stop ride"
          >
            <span className="text-[11px]" aria-hidden>◼</span>
            Stop
          </button>
        </div>
      </div>

      {/* Wave 14ca-fix64: 3-Button-Stop-Modal (Save / Discard / Continue).
          Portal damit's nicht durch die Map-Stacking-Kontexte geclipped
          wird — analog OneWayPicker. Tap-Targets >= 56px fuer Glove-Mode. */}
      {showStopModal &&
        createPortal(
          <StopRideModal
            km={km}
            climbM={climbM}
            pointCount={stats.pointCount}
            onSave={handleSave}
            onDiscard={handleDiscard}
            onCancel={() => setShowStopModal(false)}
          />,
          document.body,
        )}
    </>
  );
}

/**
 * Wave 14cf-fix6: kompakte Formatierung der Moving-Time fuer die RIDE-
 * Pille. Ziele:
 *   < 1 Stunde   →  "12m"          (zwei Stellen, kein "0h")
 *   ≥ 1 Stunde   →  "1h23"         (Doppelpunkt-frei wegen Platz; Minuten
 *                                    immer 2-stellig damit's nicht hopst)
 *   ≥ 10 Stunden →  "12h45"        (Stunden 2-stellig, Minuten 2-stellig)
 *   0 ms / null  →  "0m"           (Sentinel beim ersten Punkt vor dem
 *                                    ersten gemoveten Intervall)
 */
function formatMovingTime(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return '0m';
  const totalSec = Math.floor(ms / 1000);
  const totalMin = Math.floor(totalSec / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h${m.toString().padStart(2, '0')}`;
}

/** Stop-Confirm-Modal mit drei klar abgegrenzten Aktionen. Reihenfolge
 *  bewusst Save (gruen) → Discard (rot) → Weiterfahren (neutral): Save
 *  ist die Default-Aktion (95% der Tappings), Discard ist destruktiv und
 *  steht visuell deutlich abgesetzt, Weiterfahren ist die Escape-Hatch.
 */
function StopRideModal(props: {
  km: string;
  climbM: number;
  pointCount: number;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}) {
  const { km, climbM, pointCount, onSave, onDiscard, onCancel } = props;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Ride beenden"
      onClick={onCancel}
    >
      <div
        className="bg-turbo-900 border border-turbo-500/40 rounded-2xl p-5 w-full max-w-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white mb-1">End ride?</h3>
        <p className="text-[13px] text-white/70 mb-4 leading-snug tabular-nums">
          {km} km · ↑{climbM} m · {pointCount} points
        </p>
        <div className="space-y-2.5">
          <button
            type="button"
            onClick={onSave}
            className="w-full h-14 rounded-xl bg-emerald-500/95 hover:bg-emerald-400 active:bg-emerald-600 text-white font-bold text-base flex items-center justify-center gap-2 border border-emerald-300/50 shadow"
          >
            <span aria-hidden="true" className="text-lg leading-none">💾</span>
            <span>Stop &amp; save</span>
          </button>
          <button
            type="button"
            onClick={onDiscard}
            className="w-full h-14 rounded-xl bg-red-500/95 hover:bg-red-400 active:bg-red-600 text-white font-bold text-base flex items-center justify-center gap-2 border border-red-300/50 shadow"
          >
            <span aria-hidden="true" className="text-lg leading-none">🗑</span>
            <span>Stop &amp; discard</span>
          </button>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="mt-4 w-full h-12 rounded-xl bg-turbo-700/60 hover:bg-turbo-700 text-white/85 hover:text-white font-medium text-sm border border-white/10"
        >
          Keep riding
        </button>
      </div>
    </div>
  );
}
